import React, { useState, useEffect, useMemo } from "react";
import { Button, message as Message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useTranslation } from "react-i18next";
import Countdown, { zeroPad } from "react-countdown";
import styles from "./styles.module.less";
import classnames from "classnames";
import { i18n } from "@/locales";

import { PASSWORD_STRENGTH_TEXT_MAP } from "@/constants/enum";
import { getSymbolTypeLength } from "@/utils/formRules";
import { useSearch } from "@/hooks/useSearch";
import { parseJwt } from "@/utils";
import { useAsyncFn } from "react-use";
import { InitalPasswordForm, MobileInitalPasswordForm } from "./InitalPassword";
import { ResetPasswordForm, MobileResetPasswordForm } from "./ResetPassword";

export enum InputType {
  TEXT = "text",
  PASSWORD = "password",
}

export const ChangePassword: React.FC<{ type?: "inital" | "reset" }> = ({ type }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { initial_token: token } = useSearch();

  const onSuccess = () => {
    if (type === "inital") {
      Message.success(t("common.updatePasswordSuccess"));

      setTimeout(() => {
        autoReload();
      }, 500);
    } else {
      Message.success(t("common.updatePasswordSuccess"));

      setTimeout(() => {
        autoReload();
      }, 500);
    }
  };

  const [onFinish, onFinishFn] = useAsyncFn(async () => {
    await form.validateFields();

    const { password, oldPassword } = form.getFieldsValue();
    if (type === "inital") {
      await window.__authing__.resetPasswordByFirstLoginToken({
        token: token as string,
        // 新的密码
        password: password,
      });
    } else {
      try {
        await window.__authing__.resetPasswordByForceResetToken({
          token: token as string,
          newPassword: password,
          oldPassword: oldPassword,
        });
      } catch (error: any) {
        console.log(error);
        return;
      }
    }

    onSuccess();
  }, [form, token]);

  const expTime = useMemo(() => {
    if (token) {
      const { exp } = parseJwt(token as string);

      return exp;
    }

    return 0;
  }, [token]);

  // 规则
  const rules = useMemo(() => {
    return {
      default: {
        required: true,
        message: t("login.inputPwd"),
      },
      low: {
        validator(r: any, v: any) {
          if (v && v.length < 6) {
            return Promise.reject(PASSWORD_STRENGTH_TEXT_MAP[1].validateMessage);
          }
          return Promise.resolve();
        },
      },
      middle: {
        validator(r: any, v: any) {
          if (v && (v.length < 6 || getSymbolTypeLength(v) < 2)) {
            return Promise.reject(PASSWORD_STRENGTH_TEXT_MAP[2].validateMessage);
          }
          return Promise.resolve();
        },
      },
      high: {
        validator(r: any, v: any) {
          if (v && (v.length < 6 || getSymbolTypeLength(v) < 3)) {
            return Promise.reject(PASSWORD_STRENGTH_TEXT_MAP[3].validateMessage);
          }
          return Promise.resolve();
        },
      },
      auto: {
        pattern: window.__config__.customPasswordStrength.regex as string,
        message: window.__config__.customPasswordStrength.message as string,
      },
      passA: {
        validator: (rule: any, value: any, callback: any) => {
          const _target = form.getFieldValue("confirmPassword");
          if (_target) {
            if (value === _target) {
              form.setFieldsValue({ confirmPassword: _target });
              form.validateFields(["confirmPassword"]);
            }
            if (value && value.indexOf(" ") !== -1) {
              callback(t("common.initialPasswordNoSpace"));
            } else {
              callback();
            }
          } else {
            callback();
          }
        },
      },
      passB: {
        validator: (rule: any, value: any, callback: any) => {
          value !== form.getFieldValue("password") ? callback(t("common.confirm2Password")) : callback();
        },
      },
    };
  }, [form, t]);

  const [regexA, setRegexA] = useState([rules.default, rules.passA]);
  const [regexB, setRegexB] = useState([rules.default, rules.passB]);

  const autoReload = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (window && window.__config__.customPasswordStrength.enabled) {
      setRegexA([rules.default, rules.auto as any, rules.passA]);
      setRegexB([rules.default, rules.auto as any, rules.passB]);
    }

    if (window && window.__config__.customPasswordStrength.enabled === false && window.__config__.passwordStrength) {
      const passwordStrength = window.__config__.passwordStrength;
      // @ts-ignore
      if (passwordStrength !== 0 && passwordStrength !== 4) {
        let _rules = null;
        switch (passwordStrength) {
          case 1:
            _rules = rules.low;
            break;
          case 2:
            _rules = rules.middle;
            break;
          case 3:
            _rules = rules.high;
            break;
        }
        setRegexA([rules.default, _rules as any, rules.passA]);
        setRegexB([rules.default, _rules as any, rules.passB]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const title = useMemo(() => {
    if (type === "inital") {
      return t("common.setInitialPassword");
    } else {
      return t("common.setResetPassword");
    }
  }, [t, type]);

  const useTypeForm = useMemo(() => {
    if (type === "inital") {
      return {
        form: <InitalPasswordForm form={form} regexA={regexA} regexB={regexB} />,
        mobileForm: <MobileInitalPasswordForm form={form} regexA={regexA} regexB={regexB} />,
      };
    } else {
      return {
        form: <ResetPasswordForm form={form} regexA={regexA} regexB={regexB} />,
        mobileForm: <MobileResetPasswordForm form={form} regexA={regexA} regexB={regexB} />,
      };
    }
  }, [form, regexA, regexB, type]);

  return (
    <div>
      <div className={classnames(styles["authing-init-password-modal"])}>
        <div className={classnames(styles["authing-init-password-container"])}>
          <div
            className={classnames(styles["authing-init-password-title"], styles["authing-init-password-title-mobile"])}
          >
            <div className={classnames(styles["authing-init-password-title-icon"])}>
              <img src={window.__config__.logo} alt="" />
            </div>
            <div className={classnames(styles["authing-init-password-title-name"])}>
              <span className="authing-initia-header-title">{window.__config__.name}</span>
              <span className={`authing-initia-header-title-${i18n.language}`}></span>
            </div>
          </div>

          <div className={classnames(styles["authing-init-password-head-mobile"])}>{title}</div>

          <div
            className={classnames(styles["authing-init-password-form"], styles["authing-init-password-form-mobile"])}
          >
            <div className={classnames(styles["authing-init-password-head"])}>{title}</div>
            <div className={classnames(styles["authing-init-password-body"])}>{useTypeForm.form}</div>
            <div className={classnames(styles["authing-init-password-footer"])}>
              {/* <ToggleLang /> */}
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  onFinishFn();
                }}
                loading={onFinish.loading}
              >
                {t("common.confirm")}
              </Button>
            </div>
          </div>

          {/* 移动表单 */}
          <div className={classnames(styles["authing-init-mobile"])}>{useTypeForm.mobileForm}</div>

          {/* 移动按钮 */}
          <div className={classnames(styles["authing-init-mobile"])}>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                onFinishFn();
              }}
            >
              {t("common.confirm")}
            </Button>
          </div>
          <span className={`authing-initial-password-${i18n.language}`}></span>
          {type === "inital" && (
            <div
              className={classnames(
                styles["authing-init-password-timeout"],
                styles["authing-init-password-timeout-mobile"]
              )}
            >
              <div className={classnames(styles["authing-init-password-timeout-count-down"])}>
                {t("common.initCountDownH1")}
                <Countdown
                  date={expTime * 1000}
                  precision={3}
                  renderer={({ minutes, seconds, completed }) => {
                    if (completed) {
                      autoReload();
                    } else {
                      return (
                        <span>
                          {zeroPad(minutes)}:{zeroPad(seconds)}
                        </span>
                      );
                    }
                  }}
                />
                {t("common.initCountDownH2")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
